import { Injectable, NgZone } from '@angular/core';
import { Device } from '@ionic-native/device/ngx';
import { Market } from '@ionic-native/market/ngx';
import { AlertController, ToastController } from '@ionic/angular';
import { ChatDialogPayload, PosTerminalChatService } from './pos-terminal-chat.service';
import { PosCommandEnvelope } from './pos-command.models';
import { PosTerminalCommunicationService } from './pos-terminal-communication.service';

// Store pages of this app (the same ids AppVersionService opens).
const PLAY_MARKET_ID = 'nt.pos.terminal';
const APP_STORE_ID = 'id1563370748';
const TOAST_COLORS = ['primary', 'success', 'warning', 'danger', 'medium', 'dark'];

export interface InfoDialogPayload {
  type: 'info';
  title?: string;
  message: string;
  buttonText?: string;
}

export interface UpdateDialogPayload {
  type: 'update';
  title?: string;
  message?: string;
  // No "later" button and no closing by the backdrop.
  force?: boolean;
  // Instead of the app's store page.
  url?: string;
}

export interface ToastDialogPayload {
  type: 'toast';
  title?: string;
  message: string;
  color?: string;
  durationMs?: number;
  position?: 'top' | 'middle' | 'bottom';
}

export type DialogPayload = ChatDialogPayload | InfoDialogPayload | UpdateDialogPayload | ToastDialogPayload;

// The "dialog" admin command: something shown on the terminal screen. chat and toast have no overlay,
// info and update are alerts over the app. The command completes once it is shown, not when the user
// answers. Created at app start (AppComponent).
@Injectable({ providedIn: 'root' })
export class PosTerminalDialogService {
  // Not provided at the root (only in TerminalModule); the plugin wrapper has no dependencies.
  private readonly _market = new Market();

  constructor(
    communication: PosTerminalCommunicationService,
    private _chat: PosTerminalChatService,
    private _alerts: AlertController,
    private _toasts: ToastController,
    private _device: Device,
    private _zone: NgZone,
  ) {
    communication.register('dialog', (payload: DialogPayload, envelope) => this.show(payload, envelope));
  }

  async show(payload: DialogPayload, envelope: PosCommandEnvelope) {
    switch (payload?.type) {
      case 'chat':
        return this._chat.apply(payload, envelope);
      case 'info':
        return this._zone.run(() => this._info(payload));
      case 'update':
        return this._zone.run(() => this._update(payload));
      case 'toast':
        return this._zone.run(() => this._toast(payload));
      default:
        throw new Error(`Unknown dialog type "${(payload as { type?: string })?.type}"`);
    }
  }

  private async _info(payload: InfoDialogPayload) {
    const message = this._required(payload.message);
    const alert = await this._alerts.create({
      header: payload.title || 'Повідомлення',
      message: this._html(message),
      buttons: [payload.buttonText || 'OK'],
      cssClass: 'admin-dialog',
    });
    await alert.present();
    return { type: 'info', shown: true };
  }

  private async _update(payload: UpdateDialogPayload) {
    const force = !!payload.force;
    const open = () => {
      this._openStore(payload.url);
      // A forced update stays on screen: the user comes back to it until the app is updated.
      return !force;
    };

    const alert = await this._alerts.create({
      header: payload.title || 'Доступна нова версія',
      message: this._html(payload.message || 'Оновіть застосунок, щоб отримати нові функції та покращення.'),
      backdropDismiss: !force,
      buttons: force
        ? [{ text: 'Оновити', handler: open }]
        : [{ text: 'Пізніше', role: 'cancel' }, { text: 'Оновити', handler: open }],
      cssClass: 'admin-dialog',
    });
    await alert.present();
    return { type: 'update', shown: true, force, store: payload.url || this._storeName() };
  }

  private async _toast(payload: ToastDialogPayload) {
    const message = this._required(payload.message);
    const toast = await this._toasts.create({
      header: payload.title || undefined,
      message: this._html(message),
      color: TOAST_COLORS.includes(payload.color) ? payload.color : 'dark',
      duration: Math.min(Math.max(Number(payload.durationMs) || 5000, 1000), 60_000),
      position: ['top', 'middle', 'bottom'].includes(payload.position) ? payload.position : 'top',
      buttons: [{ icon: 'close', role: 'cancel' }],
    });
    await toast.present();
    return { type: 'toast', shown: true };
  }

  // App Store or Play Market page of this app; the web build reloads to get the new version.
  private _openStore(url?: string): void {
    if (url && /^https?:\/\//i.test(url)) {
      window.open(url, '_system');
      return;
    }

    const platform = this._device.platform;
    if (platform === 'iOS') {
      void this._market.open(APP_STORE_ID);
    } else if (platform === 'Android') {
      void this._market.open(PLAY_MARKET_ID);
    } else {
      window.location.reload();
    }
  }

  private _storeName(): string {
    const platform = this._device.platform;
    return platform === 'iOS' ? 'App Store' : platform === 'Android' ? 'Play Market' : 'reload';
  }

  private _required(text: unknown): string {
    const value = typeof text === 'string' ? text.trim() : '';
    if (!value) {
      throw new Error('Dialog message is required');
    }

    return value;
  }

  // Alert messages are HTML: the admin's text is shown as typed, line breaks kept.
  private _html(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/\n/g, '<br>');
  }
}
